import { useRef, useState } from 'react'
import { useStore } from 'zustand'
import { useModeSupport, type SessionMode } from '../core/capabilities'
import { xrStore } from '../core/xr-store'
import { Lobby } from '../ui/Lobby'
import { XRScene } from './XRScene'
import { ErrorBoundary } from './ErrorBoundary'

async function enterMode(mode: SessionMode) {
  if (mode === 'immersive-ar') await xrStore.enterAR()
  else if (mode === 'immersive-vr') await xrStore.enterVR()
}

export default function App() {
  const support = useModeSupport()
  const session = useStore(xrStore, (s) => s.session)
  const [enterError, setEnterError] = useState<string>()
  const busy = useRef(false)

  const enter = (mode: SessionMode) => {
    if (busy.current || session != null) return // never fire two requestSessions
    busy.current = true
    setEnterError(undefined)
    enterMode(mode)
      .catch((e) => setEnterError(String(e)))
      .finally(() => (busy.current = false))
  }

  // Switching modes = exit to the 2D lobby, then re-enter the other mode with a fresh user gesture.
  // Programmatic end()->requestSession() in one go wedges the Quest runtime (forces a headset
  // reboot, then NotSupportedError); routing through the lobby reuses the reliable first-entry path.
  const exitToLobby = async () => {
    if (busy.current) return
    busy.current = true
    try {
      await xrStore.getState().session?.end()
    } catch (e) {
      setEnterError(String(e))
    } finally {
      busy.current = false
    }
  }

  return (
    <ErrorBoundary>
      <div className="relative h-full w-full">
        <XRScene onExit={exitToLobby} />
        {session == null && <Lobby onEnter={enter} error={enterError} support={support} />}
      </div>
    </ErrorBoundary>
  )
}
